import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";

// Calm long-form markdown for the public legal pages (#305, DESIGN.md §3). The
// operator is the only author (behind requireAdmin), but we still render through
// `rehype-sanitize` as defense-in-depth — no raw HTML, scripts, or event handlers
// survive to the DOM. Element styles follow the Keepsake Studio system: Space
// Grotesk (`font-display`) headings, Inter body at line-height 1.6, Studio Coral
// only on links, hairline rules, quiet fills instead of accent stripes.

// A blockquote uses a Quiet Mist fill (not a coloured left stripe — DESIGN.md bans
// `border-left` accent stripes).
function Quote({ children }: { children?: ReactNode }) {
  return (
    <blockquote className="my-5 rounded-lg bg-muted/60 px-4 py-3 text-muted-foreground">
      {children}
    </blockquote>
  );
}

function Anchor({ href, children }: { href?: string; children?: ReactNode }) {
  const external = Boolean(href && /^https?:\/\//.test(href));
  return (
    <a
      href={href}
      className="text-primary underline-offset-2 hover:underline"
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

export function Markdown({ children }: { children: string }) {
  return (
    // `max-w` caps the measure at ~68ch (DESIGN.md: 65–75ch). `text-pretty` tidies
    // long prose; headings balance individually.
    <div className="max-w-[68ch] text-[1rem] leading-relaxed text-foreground [text-wrap:pretty]">
      <ReactMarkdown
        rehypePlugins={[rehypeSanitize]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-2 mb-4 font-display text-3xl font-semibold tracking-tight [text-wrap:balance]">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-10 mb-3 font-display text-xl font-semibold tracking-tight [text-wrap:balance]">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-8 mb-2 font-display text-lg font-semibold tracking-tight">
              {children}
            </h3>
          ),
          p: ({ children }) => <p className="my-4 leading-relaxed">{children}</p>,
          a: ({ href, children }) => <Anchor href={href}>{children}</Anchor>,
          ul: ({ children }) => (
            <ul className="my-4 list-disc space-y-1.5 pl-6 leading-relaxed">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-4 list-decimal space-y-1.5 pl-6 leading-relaxed">{children}</ol>
          ),
          li: ({ children }) => <li className="pl-1">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          blockquote: ({ children }) => <Quote>{children}</Quote>,
          hr: () => <hr className="my-8 border-border" />,
          code: ({ className, children }) => {
            // react-markdown v9 routes both inline code and fenced blocks through
            // this override. Fenced blocks are wrapped in `<pre>` (styled below) and
            // carry a `language-*` class or a trailing newline; those render as plain
            // mono so they don't get the inline "chip" background twice. Only true
            // inline code gets the chip.
            const isBlock =
              Boolean(className?.startsWith("language-")) || String(children).includes("\n");
            return isBlock ? (
              <code className="font-mono text-sm">{children}</code>
            ) : (
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em]">
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="my-5 overflow-x-auto rounded-lg bg-muted p-4 text-sm">{children}</pre>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
